import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useAtividades } from '../hooks/useAtividades.js';
import { AtividadesList } from '../components/crm/AtividadesList.js';
import { AtividadeForm } from '../components/crm/AtividadeForm.js';
import type { Atividade, AtividadeFormInput } from '@azoupvendas/shared';

// AIDEV-NOTE: Opções de filtro de status para a listagem de atividades
const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'realizada', label: 'Realizada' },
  { value: 'cancelada', label: 'Cancelada' }
];

export function Atividades() {
  const [formOpen, setFormOpen] = useState(false);
  const [atividadeEditando, setAtividadeEditando] = useState<Atividade | null>(null);
  const [atividadeExcluindo, setAtividadeExcluindo] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<'pendente' | 'realizada' | 'cancelada' | ''>('');
  const {
    atividades,
    isLoading,
    isError,
    createAtividade,
    completeAtividade,
    updateAtividade,
    deleteAtividade
  } = useAtividades(filtroStatus ? { status: filtroStatus } : {});

  const handleEdit = (ativ: Atividade) => setAtividadeEditando(ativ);

  const handleCloseForm = () => {
    setFormOpen(false);
    setAtividadeEditando(null);
  };

  const handleSubmit = async (data: AtividadeFormInput) => {
    if (atividadeEditando) {
      await updateAtividade.mutateAsync({ id: atividadeEditando.id, ...data });
    } else {
      await createAtividade.mutateAsync(data);
    }
  };

  const handleConfirmarExclusao = () => {
    if (atividadeExcluindo) {
      deleteAtividade.mutate(atividadeExcluindo, { onSettled: () => setAtividadeExcluindo(null) });
    }
  };

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (isError) return <Alert severity="error">Erro ao carregar atividades.</Alert>;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Atividades
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {atividades.filter((a) => a.status === 'pendente').length} pendentes
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setFormOpen(true)}>
          + Nova Atividade
        </Button>
      </Box>

      {/* Filtros */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={filtroStatus}
            onChange={(e) =>
              setFiltroStatus(e.target.value as 'pendente' | 'realizada' | 'cancelada' | '')
            }
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/* Lista */}
      <AtividadesList
        atividades={atividades}
        onComplete={(id) => completeAtividade.mutate(id)}
        onEdit={handleEdit}
        onDelete={(id) => setAtividadeExcluindo(id)}
      />

      {/* Form criação / edição */}
      <AtividadeForm
        open={formOpen || !!atividadeEditando}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        initialData={atividadeEditando ?? undefined}
      />

      {/* Confirmação de exclusão */}
      <Dialog open={!!atividadeExcluindo} onClose={() => setAtividadeExcluindo(null)}>
        <DialogTitle>Excluir atividade</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir esta atividade? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAtividadeExcluindo(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmarExclusao}
            disabled={deleteAtividade.isPending}
          >
            {deleteAtividade.isPending ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
